/**
 * 表格绘制的方法mixins
 * 涉及的所有图表说明
 * 单柱 + 堆叠
 * 条形图 + 条形堆叠
 * 折线
 * 饼图 + 环形图
 * 雷达
 * 仪表盘
 */
import { ChartDataType, ChartType } from '../../enums';
import { toThousands, multipliedBy } from '../../utils';
const lightColors = ['#5B8FF9', '#61DDAA', '#65789B', '#FAD337', '#F6903D', '#7262FD', '#9661BC', '#008685', '#F08BB4',
  '#CDDDFD', '#CDF3E4', '#CED4DE', '#FCEBB9', '#FFE0C7', '#D3CEFD', '#DECFEA', '#BBDEDE', '#FFE0ED'];
const darkColors = ['#4EA5F4', '#436BFF', '#2E4DD9', '#1D33B3', '#0F1E8C', '#0A1266', '#000000', '#0A1266', '#0F1E8C', '#1D33B3', '#2E4DD9', '#436BFF'];
const SPLIT_NUMBER = 5; // 数值坐标轴分割数
const SPLIT_VALUE = 5; // 坐标轴最小跨度

export default {
  data() {
    return {
      chart: null
    };
  },
  props: {
    chartType: {
      type: Number
    },
    sliderScoreDownload: {
      type: Boolean,
      default: false
    },
    dataList: {
      type: Array,
      default: () => []
    },
    specialData: { // nps的净值推荐 特征数据
      type: Object,
      default: () => ({})
    },
    total: {
      type: Number,
      default: 0
    },
    dataType: {
      type: Number,
      default: ChartDataType.number
    }
  },
  computed: {
    exporting() {
      return this.$route.query.exporting;
    },
    showPercent() {
      return this.dataType === ChartDataType.percent;
    },
    // 单组时需要展示的图例
    labelList() {
      if (this.dataList && this.dataList.length) {
        return this.dataList[0].data;
      }
      return [];
    },
    splitNumber() {
      return this.chartType === ChartType.strip ? SPLIT_NUMBER * 2 : SPLIT_NUMBER;
    }

  },
  watch: {
    chartType(val) {
      this.drawChart(this.$refs.chart, val);
    },
    dataType() {
      this.drawChart(this.$refs.chart, this.chartType);
    },
    dataList() {
      this.drawChart(this.$refs.chart, this.chartType);
    }
  },
  beforeDestroy() {
    this.chart && this.chart.dispose();
  },
  methods: {
    getLightColor(index) {
      const idx = index % lightColors.length;
      return lightColors[idx];
    },
    /**
     * 绘制方法
     * @param {*} dom
     * @param {*} type
     */
    drawChart(dom, type) {
      if (!this.chart) this.chart = window.echarts.init(dom);
      this.chart.clear();
      const option = this._getChartOption(type);
      option.animation = !this.exporting;
      this.chart.setOption(option);
    },
    /**
     * 获取图标的配置信息
     * @param {*} type
     * @returns
     */
    _getChartOption(type) {
      switch (type) {
        case ChartType.circle:
        case ChartType.pie:
          return this._getPieChartOption(type === ChartType.circle);
        case ChartType.bar:
        case ChartType.line:
          return this._getBarChartOption(type);
        case ChartType.stack:
          return this._getBarChartOption(type);
        case ChartType.strip:
          return this._getStripChartOption(type);
        case ChartType.radar:
          return this._getRadarChartOption(type);
        case ChartType.gauge:
          return this._getGaugeChartOption(type);
        default:
          break;
      }
    },
    /**
     * 获取饼图和环形图的option
     * @param {*} isCircle 是否是环形图
     */
    _getPieChartOption(isCircle) {
      const labelList = JSON.parse(JSON.stringify(this.labelList));
      const dataList = JSON.parse(JSON.stringify(this.dataList));
      const max = labelList && labelList.findMaxByProp('value') || {};
      return {
        title: {
          show: isCircle,
          text: max.name, // 最大值
          left: 'center',
          top: '35.5%',
          subtext: this.showPercent ? max.rate : toThousands(max.value),
          textStyle: {
            color: '#8C8C8C',
            fontSize: 12,
            width: 130,
            overflow: 'truncate'
          },
          itemGap: 15,
          subtextStyle: {
            color: '#262626',
            fontSize: 28,
            fontWeight: 'bold'
          }
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter: this.getTooltipFormat,
          appendToBody: true
        },
        color: lightColors,
        series: dataList.map(item => {
          return {
            name: item.name,
            type: 'pie',
            radius: isCircle ? ['75%', '100%'] : '100%',
            avoidLabelOverlap: true,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2
            },
            emphasis: {
              scale: false
            },
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            data: item.data
          };
        }) || []

      };
    },

    /**
     * 获取雷达图的option
     */
    _getRadarChartOption() {
      return {
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'none'
          },
          confine: true,
          formatter: this.getTooltipFormat,
          appendToBody: true
        },
        radar: {
          // shape: 'circle',
          nameGap: 10,
          indicator: this.labelList.map((item, index) => {
            return {
              name: item.name, max: this.getActualMaxValue(), axisLabel: { show: index === 0, fontSize: 8 }
            };
          }),
          axisLabel: {
            formatter: (value, index) => {
              if (this.showPercent) return index * 20 + '%';
              return value;
            }
          },
          splitArea: {
            areaStyle: {
              color: ['#F2F2F2', '#fafafa']
            }
          },
          axisLine: {
            lineStyle: {
              color: '#000',
              opacity: 0.06
            }
          },
          splitLine: {
            lineStyle: {
              color: '#000',
              opacity: 0.06
            },
            symbolSize: [4, 4]
          }
        },
        color: ['#3196FA'],
        series: [{
          type: 'radar',
          symbolSize: 4,
          areaStyle: {
            color: '#5B8FF9',
            opacity: 0.2
          },
          lineStyle: {
            color: '#5B8FF9',
            width: 1
          },
          itemStyle: {
            color: '#5B8FF9'
          },
          data: [
            {
              value: this.labelList.map(item => item.value),
              name: ' '
            }
          ]
        }]
      };
    },
    /**
     *
     * @returns 获取仪表盘的option
     */
    _getGaugeChartOption() {
      const val = multipliedBy(this.specialData.netPromoterRate, 100);
      const angle = 90 - 150 * val / 100;
      const width = 280;

      return {
        tooltip: {
          show: false
        },

        series: [
          { // 灰色背景 + axisTick
            center: ['50%', '50%'],
            radius: 0.35 * width,
            type: 'gauge',
            startAngle: 240,
            endAngle: -60,
            min: -100,
            max: 100,
            progress: {
              show: false
            },
            pointer: {
              show: false
            },
            axisTick: {
              // show: false,
              distance: -0.1 * width,
              splitNumber: 5,
              lineStyle: {
                width: 2,
                type: 'solid',
                color: '#D8D8D8'
              },
              length: 2
            },
            splitLine: {
              show: false
            },
            axisLabel: {
              show: false
            },
            axisLine: {
              lineStyle: {
                width: 0.06 * width,
                color: [[1, '#F5F5F7']]
              }
            }
          },
          { // label
            center: ['50%', '50%'],
            radius: 0.35 * width,
            type: 'gauge',
            startAngle: 240,
            endAngle: -60,
            min: -100,
            max: 100,
            progress: {
              show: false
            },
            pointer: {
              show: false
            },
            axisTick: {
              distance: -0.1 * width,
              splitNumber: 1,
              lineStyle: {
                width: 2,
                type: 'solid',
                color: '#fff'
              },
              length: 2
            },
            splitLine: {
              show: false
            },
            axisLabel: {
              distance: -0.16 * width,
              color: '#BFBFBF',
              fontSize: 0.045 * width,
              padding: [-0.015 * width, -0.04 * width, -0.015 * width, -0.06 * width]
              // show: false
            },
            axisLine: {
              lineStyle: {
                width: 0.06 * width,
                color: [[1, '#F5F5F7']]
              }
            }
          },
          { // 中间阴影
            center: ['50%', '50%'],
            radius: 0.35 * width - 0.06 * width,
            type: 'gauge',
            startAngle: 300,
            endAngle: -60,
            min: -100,
            max: 100,
            tooltip: {
              show: false
            },
            progress: {
              show: false
            },
            pointer: {
              show: false
            },
            axisTick: {
              show: false
            },
            splitLine: {
              show: false
            },
            axisLine: {
              lineStyle: {
                width: 0.35 * width - 0.06 * width,
                color: [[1, '#fff']],
                shadowColor: 'rgba(2, 30, 89, 0.15)',
                shadowBlur: 25
              }
            },
            axisLabel: {
              show: false
            }
          },
          { // 条形颜色 + 指针 + 中间
            center: ['50%', '50%'],
            radius: 0.35 * width - 0.06 * width + 0.01 * width,
            type: 'gauge',
            startAngle: 240,
            endAngle: angle,
            min: -100,
            max: 100,
            progress: {
              show: false
            },

            axisTick: {
              show: false
            },
            splitLine: {
              show: false
            },
            pointer: {
              showAbove: true,
              length: width * 0.2 * 25 / 50,
              width: width * 0.2 * 21 / 50,
              offsetCenter: [0, -(0.35 * width - 0.06 * width + 0.01 * width) + width * 0.1 * 25 / 50]
            },
            axisLabel: {
              show: false
            },
            axisLine: {
              roundCap: true,
              lineStyle: {
                width: 0.02 * width,
                color: [[1, '#5B8FF9']]
              }
            },
            detail: {
              formatter: (value) => {
                return `{number|${val}}\n{label|${this.$t('pc_ote_lbl_networthrecommend')}}`;
              },
              offsetCenter: [0, 0],
              rich: {
                number: {
                  color: '#262626',
                  fontSize: 32,
                  lineHeight: 50,
                  fontWeight: 600
                },
                label: {
                  color: '#8C8C8C',
                  fontSize: 18
                }
              }
            },
            data: [{ value: 100 }]
          }

        ]
      };
    },
    /**
     * 获取柱状图、折线图的option
     * @param type
     * @returns
     */
    _getBarChartOption(type) {
      const base = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: type === ChartType.line ? 'line' : 'shadow'
          },
          confine: true,
          formatter: this.getTooltipFormat,
          appendToBody: true
        },
        grid: {
          left: 0,
          top: 10,
          right: 0,
          bottom: 10
        },
        color: this.dataList.length <= 1 ? ['#5B8FF9'] : darkColors,
        xAxis: {
          type: 'category',
          data: this.labelList.map(item => item.name),
          axisLabel: {
            show: false
          },
          axisTick: {
            show: false
          },
          boundaryGap: ['5%', '5%'],
          axisLine: {
            lineStyle: {
              color: '#D8D8D8'
            }
          }
        },
        yAxis: {
          type: 'value',
          interval: this.getActualMaxValue() / this.splitNumber,
          // splitNumber: this.splitNumber,
          max: this.getActualMaxValue(),
          splitLine: {
            lineStyle: {
              color: '#D8D8D8',
              type: 'dashed'
            }
          },
          axisLabel: {
            show: false
          },
          min: 0
        },
        series: this._getBarChartSeries(type)
      };
      if (this.sliderScoreDownload) {
        base.grid = { left: 25, top: 10, right: 0, bottom: 20 };
        base.xAxis.axisLabel.show = true;
        base.xAxis.axisTick.show = true;
        base.xAxis.axisLine = {};
        base.yAxis.axisLabel.show = true;
        base.yAxis.max = undefined;
        base.yAxis.interval = undefined;
      }
      return base;
    },
    /**
     * 获取条形图的option
     * @param {*} type
     * @returns
     */
    _getStripChartOption(type) {
      const base = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          confine: true,
          formatter: this.getTooltipFormat,
          appendToBody: true
        },
        grid: {
          left: 0,
          top: 0,
          right: 20,
          bottom: 0
        },
        color: this.dataList.length > 1 ? darkColors : ['#5B8FF9'], // ['#5B8FF9'],
        yAxis: {
          type: 'category',
          data: this.labelList.map(item => item.name),
          axisLabel: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: '#D8D8D8'
            }
          }
        },
        xAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#D8D8D8',
              type: 'dashed'
            }
          },
          axisLabel: {
            show: false
          },
          interval: this.getActualMaxValue() / this.splitNumber,
          max: this.getActualMaxValue()
        },
        series: this._getBarChartSeries(type)
      };
      if (this.sliderScoreDownload) {
        base.grid = { left: 30, top: 5, right: 10, bottom: 20 };
        base.yAxis.axisLabel.show = true;
        base.yAxis.axisTick.show = true;
        base.yAxis.axisLine = {};
        base.xAxis.axisLabel.show = true;
        base.xAxis.max = undefined;
        base.xAxis.interval = undefined;
      }
      return base;
    },
    _getBarChartSeries(type) {
      const dataList = JSON.parse(JSON.stringify(this.dataList));
      if (type === ChartType.line) {
        return dataList ? dataList.map((item, index) => {
          const color = darkColors[index % darkColors.length];
          return {
            connectNulls: true,
            name: item.name,
            data: item.data.map(d => ({ value: d.value, rate: d.rate, txtValue: d.txtValue })),
            type: 'line',
            lineStyle: {
              width: 1
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 0.8,
                colorStops: [{ offset: 0, color: dataList.length <= 1 ? '#5B8FF9' : color }, { offset: 1, color: 'rgba(255,255,255,0.3)' }]
              }
            }
          };
        }) : [];
      }

      // 条形 & 柱状
      return dataList ? dataList.map(item => {
        return {
          name: item.name,
          type: 'bar',
          stack: 'total',
          // // https://echarts.apache.org/zh/option.html#series-bar.label
          // label: {
          //   show: true,
          //   position: 'top',
          //   color: '#8C8C8C'
          // },
          barWidth: this.sliderScoreDownload ? 5 : 24,
          emphasis: {
            // focus: 'series',
            // blurScope: 'series'
            itemStyle: {
              shadowColor: 'rgba(0, 0, 0, 0.3)',
              shadowBlur: 5
            }
          },
          data: item.data.map(d => ({ value: d.value, rate: d.rate }))
        };
      }) : [];
    },
    // 图表数值轴的实际最大值
    getMaxValue() {
      let labelMax = 0;
      const list = JSON.parse(JSON.stringify(this.dataList));
      const isStack = this.chartType === ChartType.bar || this.chartType === ChartType.strip;
      if (list && list.length) {
        const length = list[0].data.length;
        const array = Array(length);
        for (let i = 0; i < length; i++) {
          let max = 0;
          for (let j = 0; j < list.length; j++) {
            const item = list[j].data[i] || {};
            if (isStack) {
              max += isNaN(item.value) ? 0 : item.value;
            } else {
              max = Math.max(max, isNaN(item.value) ? 0 : item.value);
            }
          }
          array[i] = max;
        }
        labelMax = Math.max.apply(Math, array);
      }
      return labelMax;
    },
    // 获取实际坐标轴的最大值
    getActualMaxValue() {
      if (this.showPercent) return 100;
      const labelMax = this.getMaxValue();
      let splitValue = SPLIT_VALUE;
      if (labelMax < 5) splitValue = 1;
      else if (labelMax < 10) splitValue = 2;
      else if (labelMax < 25) splitValue = 5;
      else {
        splitValue = parseInt(labelMax / SPLIT_NUMBER, 10);
        splitValue = Math.ceil(splitValue / 10) * 10;
        if (splitValue > 100) {
          splitValue = Math.ceil(splitValue / 100) * 100;
        }
      }
      return splitValue * this.splitNumber;
    },
    // 数值坐标轴显示
    getValueLabelData() {
      const labelMax = this.getActualMaxValue();
      const splitNumber = this.splitNumber;
      const splitValue = labelMax / splitNumber;
      // if (this.showPercent) {
      //   splitValue = this.chartType === ChartType.strip ? 2 * SPLIT_VALUE : 4 * SPLIT_VALUE
      // } else {
      //   if (labelMax < 5) splitValue = 1
      //   else if (labelMax < 10) splitValue = 2
      //   splitValue = parseInt(labelMax / SPLIT_NUMBER) + splitValue
      // }
      const arr = this.showPercent ? ['0%'] : [0];
      for (let i = 0; i < splitNumber; i++) {
        arr.push(splitValue * (i + 1) + (this.showPercent ? '%' : ''));
      }
      return arr;
    },
    // 类型坐标轴显示
    getCategoryLabelData() {
      return this.labelList.map(item => item.name);
    },
    getTooltipFormat(params) {
      if (params instanceof Array && params.length > 1) {
        // 多柱
        const name = params[0].name;
        return `<div>
        <div class="standard-size-14 yxt-weight-5">${name}</div>
        ${params.map(item => this.getItemTemplate(item, true)).join('')}
        </div>`;
      } else {
        if (params instanceof Array) params = params[0];
        if (params.seriesType === 'radar') {
          return `<div>
        ${this.labelList.map(item => this.getRadarTemplate(item)).join('')}
        </div>`;
        }
        return this.getItemTemplate(params);
      }
    },
    getItemTemplate(params, isMulti) {
      return `<div class="flex flex-mid standard-size-12 lh20">
        <div class="tooltip-icon" style="background-color:${params.color};display:${isMulti ? '' : 'none'}"></div>
        <div class="tooltip-name flex-1">${isMulti ? params.seriesName : params.name}</div>
        <div class="tooltip-value ml40">${this.showPercent ? params.data.rate : (params.data.txtValue || params.data.value)}</div>
        </div>`;
    },
    getRadarTemplate(params) {
      return `<div class="flex flex-mid  standard-size-12">
      <div class="tooltip-name flex-1">${params.name}</div>
      <div class="tooltip-value ml32">${this.showPercent ? params.rate : params.value}</div>
      </div>`;
    }

  }
};
